import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Heading01Icon,
  Heading02Icon,
  Image01Icon,
  LeftToRightBlockQuoteIcon,
  LeftToRightListBulletIcon,
  LeftToRightListNumberIcon,
  Link02Icon,
  ParagraphIcon,
  Redo02Icon,
  SolidLine01Icon,
  SourceCodeSquareIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  TextBoldIcon,
  TextColorIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextUnderlineIcon,
  Undo02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import Color from '@tiptap/extension-color';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { uploadSigned } from 'helpers/aws-s3';
import { useClickOutside } from 'hooks/click-outside';
import { useApiUploadActions } from 'hooks/upload-actions';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import * as mime from 'react-native-mime-types';
import ImageResize from 'tiptap-extension-resize-image';

import { FloatingInput } from 'components/FormComponents';

import styles from './RichTextEditor.module.scss';

const extensions = [
  StarterKit,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Underline,
  Link.configure({ openOnClick: false }),
  TextStyle,
  Color,
  CodeBlock,
  Blockquote,
  HorizontalRule,
  ImageResize,
  Image.configure({
    HTMLAttributes: {
      class: 'rich-text-image',
    },
  }),
];

export const RichTextEditor = ({
  onChange = () => {},
  value,
  className,
  error,
  bucketFolder = 'company_documents',
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [color, setColor] = useState('#000000');
  const { getUpload, createUpload, signUpload } = useApiUploadActions();
  const editor = useEditor({
    extensions,
    content: value,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  const [showFloatingInput, setShowFloatingInput] = useState(false);
  const [currentHref, setCurrentHref] = useState('');

  const editorWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  useClickOutside(editorWrapperRef, () => setShowPicker(false));

  const setLink = useCallback(
    v => {
      if (v) {
        editor.chain().focus().setLink({ href: v }).run();
      } else {
        editor.chain().focus().unsetLink().run(); // Optional: remove link if empty
      }
      setShowFloatingInput(false);
      setCurrentHref(''); // Reset
    },
    [editor],
  );

  const openLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href || '';
    setCurrentHref(previousUrl); // Save current href
    setShowFloatingInput(true);
  }, [editor]);

  const handleColorChange = useCallback(
    color => {
      setColor(color.hex);
      editor.chain().focus().setColor(color.hex).run();
    },
    [editor],
  );

  const handleImageUpload = useCallback(
    async file => {
      try {
        const signedFile = await signUpload({
          file_name: file.name,
          file_type: file.type,
          file_ext: mime.extension(file.type),
          bucket_folder: bucketFolder,
        });

        // Upload to S3
        await uploadSigned(signedFile.url, file, file.type);

        const upload = await createUpload({
          file_name: signedFile.file_name,
          file_type: signedFile.file_type,
          hash_key: signedFile.key,
          metadata: {
            rich_text_editor: true,
          },
          modelParams: { rich_text_editor: true },
        });

        const { url } = await getUpload(upload?.id);

        editor.chain().focus().setImage({ src: url }).run();
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    },
    [editor, createUpload, signUpload],
  );

  const MENU_ITEMS = useMemo(
    () => [
      {
        label: 'Undo',
        onClick: editor => editor.chain().focus().undo().run(),
        icon: Undo02Icon,
        isActive: () => false,
        size: 16,
      },
      {
        label: 'Redo',
        onClick: editor => editor.chain().focus().redo().run(),
        icon: Redo02Icon,
        isActive: () => false,
        size: 16,
      },
      {
        label: 'Bold',
        onClick: editor => editor.chain().focus().toggleBold().run(),
        icon: TextBoldIcon,
        isActive: editor => editor.isActive('bold'),
        size: 16,
      },
      {
        label: 'Italic',
        onClick: editor => editor.chain().focus().toggleItalic().run(),
        icon: TextItalicIcon,
        isActive: editor => editor.isActive('italic'),
        size: 16,
      },
      {
        label: 'Underline',
        onClick: editor => editor.chain().focus().toggleUnderline().run(),
        icon: TextUnderlineIcon,
        isActive: editor => editor.isActive('underline'),
        size: 16,
      },
      {
        label: 'Strike',
        onClick: editor => editor.chain().focus().toggleStrike().run(),
        icon: TextStrikethroughIcon,
        isActive: editor => editor.isActive('strike'),
        size: 16,
      },
      {
        label: 'Bullet List',
        onClick: editor => editor.chain().focus().toggleBulletList().run(),
        icon: LeftToRightListBulletIcon,
        isActive: editor => editor.isActive('bulletList'),
        size: 18,
      },
      {
        label: 'Ordered List',
        onClick: editor => editor.chain().focus().toggleOrderedList().run(),
        icon: LeftToRightListNumberIcon,
        isActive: editor => editor.isActive('orderedList'),
        size: 18,
      },
      {
        label: 'H1',
        onClick: editor => editor.chain().focus().toggleHeading({ level: 1 }).run(),
        icon: Heading01Icon,
        isActive: editor => editor.isActive('heading', { level: 1 }),
        size: 18,
      },
      {
        label: 'H2',
        onClick: editor => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        icon: Heading02Icon,
        isActive: editor => editor.isActive('heading', { level: 2 }),
        size: 18,
      },
      {
        label: 'Paragraph',
        onClick: editor => editor.chain().focus().setParagraph().run(),
        icon: ParagraphIcon,
        isActive: editor => editor.isActive('paragraph'),
        size: 16,
      },
      {
        label: 'Text Color',
        onClick: () => setShowPicker(true),
        icon: TextColorIcon,
        isActive: editor => editor.isActive({ textStyle: 'color' }),
        size: 16,
      },
      {
        label: 'Align Left',
        onClick: editor => editor.chain().focus().setTextAlign('left').run(),
        icon: TextAlignLeftIcon,
        isActive: editor => editor.isActive({ textAlign: 'left' }),
        size: 16,
      },
      {
        label: 'Align Center',
        onClick: editor => editor.chain().focus().setTextAlign('center').run(),
        icon: TextAlignCenterIcon,
        isActive: editor => editor.isActive({ textAlign: 'center' }),
        size: 16,
      },
      {
        label: 'Align Right',
        onClick: editor => editor.chain().focus().setTextAlign('right').run(),
        icon: TextAlignRightIcon,
        isActive: editor => editor.isActive({ textAlign: 'right' }),
        size: 16,
      },
      {
        label: 'Image',
        onClick: () => fileInputRef.current?.click(),
        icon: Image01Icon,
        isActive: () => false,
        size: 18,
      },
      {
        label: 'Set Link',
        onClick: openLink,
        icon: Link02Icon,
        isActive: editor => editor.isActive('link'),
        size: 18,
      },
      {
        label: 'Code Block',
        onClick: editor => editor.chain().focus().toggleCodeBlock().run(),
        icon: SourceCodeSquareIcon,
        isActive: editor => editor.isActive('codeBlock'),
        size: 18,
      },
      {
        label: 'Blockquote',
        onClick: editor => editor.chain().focus().toggleBlockquote().run(),
        icon: LeftToRightBlockQuoteIcon,
        isActive: editor => editor.isActive('blockquote'),
        size: 18,
      },
      {
        label: 'Horizontal Rule',
        onClick: editor => editor.chain().focus().setHorizontalRule().run(),
        icon: SolidLine01Icon,
        isActive: editor => editor.isActive('horizontalRule'),
        size: 18,
      },
    ],
    [editor],
  );

  if (!editor) return null;

  return (
    <div
      className={cn(
        styles.root,
        'd-flex flex-column u-border u-border-radius surface-secondary',
        className,
        { [styles.error]: error?.message },
      )}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) {
            handleImageUpload(file);
          }
          e.target.value = ''; // Reset input
        }}
      />
      {/* Toolbar */}
      <div className={cn(styles.toolbar, 'd-flex u-gap-medium align-items-center flex-wrap')}>
        {MENU_ITEMS.map((item, index) => (
          <div key={index} className={item.label === 'Text Color' ? 'u-relative' : ''}>
            <OverlayTrigger
              trigger={item.label === undefined ? 'none' : ['hover', 'focus', 'click']}
              placement="top"
              overlay={<Tooltip>{item.label}</Tooltip>}>
              <HugeiconsIcon
                size={item.size}
                icon={item.icon}
                color={
                  item.label === 'Text Color'
                    ? color
                    : item.isActive(editor)
                      ? '#390a98'
                      : '#2d264d'
                }
                strokeWidth={item.label === 'Bold' || item.isActive(editor) ? 2.5 : 1.5}
                className={cn(styles.iconButton, 'u-pointer')}
                onClick={() => item.onClick(editor)}
              />
            </OverlayTrigger>
            {item.label === 'Text Color' && showPicker && (
              <div ref={editorWrapperRef} className={cn(styles.colorPicker, 'u-z-index-10')}>
                <SketchPicker color={color} onChangeComplete={handleColorChange} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={cn('u-border-bottom')} />
      {/* Editor Area */}
      <div className={cn(styles.contentWrapper)}>
        {showFloatingInput && (
          <FloatingInput
            onClick={v => setLink(v)}
            defaultValue={currentHref}
            onClose={() => setShowFloatingInput(false)}
          />
        )}
        <EditorContent
          editor={editor}
          role="presentation"
          className={cn(styles.simpleEditorContent, 'u-padding primary-text u-text-cursor')}
        />
      </div>
    </div>
  );
};
